/**
 * Till Close Service
 *
 * Clean close-till flow extracted from cashToKeep.js.
 *
 * Flow:
 *   1. Print receipt IMMEDIATELY (don't wait for API)
 *   2. Build payload from unsynced events
 *   3. Call server API (best-effort, don't block logout)
 *   4. Logout (always, regardless of API result)
 *
 * If API fails, event is marked isSynced=0 for retry on next login (via syncOfflineDta).
 */
import db from "../database";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { tillClosingPrint } from "../lib/printer/tillCloseReceipt";
import TillCloseHCPrint from "../lib/printer/tillCloseHCPrinter";
import TillCloseCWHCPrint from "../lib/printer/tillCloseCWHCprinter";
import { upsertTill, upsertPOSActivity } from "./tillServices";
import useAuthStore from "../stores/useAuthStore";
import useSessionStore from "../stores/useSessionStore";
import { trace } from "../utils/debugTrace";
import env from "../config/env";

// ── Helpers ────────────────────────────────────────────────────────────────────

function generateId() {
  return uuidv4().replace(/-/g, "").toUpperCase();
}

function now() {
  return dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
}

// ── Get unsynced events ────────────────────────────────────────────────────────

async function getUnsyncedEvents() {
  const allEvents = await db.tillEvents.toArray();
  const valid = [];

  for (const e of allEvents) {
    const isSyncNeeded = !e.isSynced || e.isSynced === 0 || e.isSynced === "0";
    if (!isSyncNeeded) continue;

    const hasData = e.allPaymentsData && e.allPaymentsData.length > 0;
    if (!hasData) {
      // Quarantine corrupted events instead of silently skipping
      try {
        if (window.electronAPI?.dataHealth) {
          // Report to main process for quarantine tracking
          console.debug("[TillClose] Quarantining corrupted till event:", e.id, "— missing allPaymentsData");
        }
      } catch {}
      continue;
    }
    valid.push(e);
  }

  return valid;
}

// ── Build tillCash payload ─────────────────────────────────────────────────────

function buildCashDenominationEvents(denominations, prc) {
  return (denominations || []).map((d) => ({
    cwrCashEventdetailsID: generateId(),
    count: d.count,
    amount: parseFloat(d.amount.toFixed(prc)),
    denomination: d.value,
  }));
}

function buildCashInOutEntries(cashInCashOutData) {
  let cashIn = 0,
    cashOut = 0,
    pettyCashIn = 0,
    pettyCashOut = 0;
  const entries = [];

  for (const item of cashInCashOutData) {
    const amount = parseFloat(item.amount);
    const time = dayjs(item.date, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD HH:mm:ss");
    const timestamp = now();
    const base = { cwrTillCashInOutId: item.key, amount, time, updated: timestamp, created: timestamp, description: item.note || "" };

    if (item.type === "cashIn") {
      cashIn += amount;
      entries.push({ ...base, type: "CI" });
    } else if (item.type === "cashOut") {
      cashOut += amount;
      entries.push({ ...base, type: "CO", paymentMode: "cash" });
    }
    if (item.type === "pettyCashIn") {
      pettyCashIn += amount;
      entries.push({ ...base, type: "PCI" });
    }
    if (item.type === "pettyCashOut") {
      pettyCashOut += amount;
      entries.push({ ...base, type: "PCO", paymentMode: "cash" });
    }
    if (item.type === "bankTransfer") {
      cashOut += amount;
      entries.push({ ...base, type: "CO", paymentMode: "cash" });
    }
  }

  return { entries, cashIn, cashOut, pettyCashIn, pettyCashOut };
}

function getMatchedTillSession(ordersDataArr, tillSessionId) {
  let matched = {};
  ordersDataArr.forEach((item) => {
    if (item.tillSessionId === tillSessionId) {
      matched = item;
    }
  });
  return matched;
}

async function buildTillCashForEvent(event, { tillData, noteValue, cashToKeep, checkedValue, posConfig }) {
  const prc = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
  const tillCash = [];
  const tillRegistration = await db.tillRegistrations.toArray();
  const retainAmt = checkedValue === "0" && posConfig?.manualCashToKeep === "N" ? useSessionStore.getState().tillSession?.totalOpeningAmount : cashToKeep || 0;

  for (const paymentObj of event.allPaymentsData) {
    if (paymentObj.name.toLowerCase() === "cash") {
      // Opening/closing denomination events
      const openedCashEvents = buildCashDenominationEvents(event.openingCashDenominations, prc);
      const closedCashEvents = buildCashDenominationEvents(event.closingCashDenominations, prc);

      // Set notes on event
      db.tillEvents.where("tillSessionId").equals(event.tillSessionId).modify({ notes: noteValue });

      // Cash in/out entries
      const cashInCashOutData = await db.cashInCashOut.toArray();
      const cio = buildCashInOutEntries(cashInCashOutData);

      // Matched orders data
      const ordersDataArr = await db.ordersData.toArray();
      const matched = getMatchedTillSession(ordersDataArr, event.tillSessionId);

      // [DI-14] Guard against division by zero when grossTotal/grossReturnTotal is 0
      const netSalesAmount = paymentObj.cashSaleAmount > 0 && matched.grossTotal ? parseFloat(matched.netTotal * (paymentObj.cashSaleAmount / matched.grossTotal)) : 0;
      const returnTaxAmount =
        Math.abs(matched.totaReturnlTax) > 0 && Math.abs(paymentObj.paymentReturn) > 0 && Math.abs(matched.grossReturnTotal) > 0
          ? matched.netReturnTotal * (paymentObj.paymentReturn / matched.grossReturnTotal)
          : 0;

      const tillCashData = (event.serverTillCash || []).filter((ele) => ele.finPaymentmethodId === paymentObj.finPaymentmethodId);

      tillCash.push({
        cwrTillCashId: tillCashData.length > 0 ? tillCashData[0].cwrTillCashId : generateId(),
        date: dayjs(event.openingTime).format("YYYY-MM-DD"),
        finPaymentmethodId: paymentObj.finPaymentmethodId,
        finFinancialAccount2Id: paymentObj.finDayCloseAccountId || null,
        opening: event.openingCash,
        opentime: dayjs(event.openingTime).format("YYYY-MM-DD HH:mm:ss"),
        closetime: now(),
        sales: parseFloat((paymentObj?.cashSaleAmount ?? 0).toFixed(prc)),
        netsales: netSalesAmount > 0 ? parseFloat(netSalesAmount.toFixed(prc)) : 0,
        cashin: cio.cashIn,
        cashout: cio.cashOut,
        retainAmt,
        closing: posConfig?.retainCashforNextShift !== "Y" ? parseFloat((event.openingCash + event.closingCash)?.toFixed(prc)) : parseFloat(event.closingCash?.toFixed(prc)),
        returns: parseFloat(paymentObj.paymentReturn.toFixed(prc)),
        netReturns: parseFloat(returnTaxAmount.toFixed(prc)),
        iscash: 1,
        isclose: 1,
        notes: noteValue,
        pettyCashIn: cio.pettyCashIn,
        pettyCashOut: cio.pettyCashOut,
        cashInOut: cio.entries,
        cashEvents: [
          {
            cwrCashEventsID: generateId(),
            amount: parseFloat(event.openingCash.toFixed(prc)),
            expected: 0,
            diff: 0,
            transactionCount: 0,
            type: "Open",
            description: "",
            cashEventDetails: openedCashEvents,
          },
          {
            cwrCashEventsID: generateId(),
            amount: parseFloat(event.closingCash.toFixed(prc)),
            expected: parseFloat(paymentObj.expectedAmount.toFixed(prc)),
            diff: parseFloat((paymentObj.difference || 0).toFixed(prc)),
            transactionCount: paymentObj.transactionCount,
            type: "Close",
            description: "",
            cashToKeep: retainAmt,
            cashEventDetails: closedCashEvents,
          },
        ],
      });
    } else {
      // Non-cash payment
      const ordersDataArr = await db.ordersData.toArray();
      const matched = getMatchedTillSession(ordersDataArr, event.tillSessionId);

      // [DI-14] Guard against division by zero when grossTotal/grossReturnTotal is 0
      const netSalesAmount = paymentObj.expectedAmount > 0 && matched.grossTotal ? parseFloat(matched.netTotal * (paymentObj.expectedAmount / matched.grossTotal)) : 0;
      const returnTaxAmount =
        Math.abs(matched.totaReturnlTax) > 0 && Math.abs(paymentObj.paymentReturn) > 0 && Math.abs(matched.grossReturnTotal) > 0
          ? matched.netReturnTotal * (paymentObj.paymentReturn / matched.grossReturnTotal)
          : 0;

      tillCash.push({
        cwrTillCashId: generateId(),
        date: dayjs(event.openingTime).format("YYYY-MM-DD"),
        finPaymentmethodId: paymentObj.finPaymentmethodId,
        finFinancialAccount2Id: paymentObj.finDayCloseAccountId || null,
        opening: 0,
        opentime: dayjs(event.openingTime).format("YYYY-MM-DD HH:mm:ss"),
        closetime: now(),
        sales: paymentObj.name.toLowerCase() !== "cafe tips" ? parseFloat(paymentObj.actualAmount.toFixed(prc)) : 0,
        netsales: netSalesAmount > 0 ? parseFloat(netSalesAmount.toFixed(prc)) : 0,
        cashin: 0,
        cashout: 0,
        retainAmt: 0,
        notes: noteValue,
        closing: paymentObj.difference === 0 ? parseFloat(parseFloat(paymentObj.amount).toFixed(2)) : parseFloat(parseFloat(paymentObj.actualAmount).toFixed(2)),
        returns: parseFloat(paymentObj.paymentReturn.toFixed(prc)),
        netReturns: parseFloat(returnTaxAmount.toFixed(prc)),
        iscash: 0,
        isclose: 1,
        pettyCashIn: 0,
        pettyCashOut: 0,
        cashInOut: [],
        cashEvents: [
          {
            cwrCashEventsID: generateId(),
            amount: paymentObj.difference === 0 ? parseFloat(paymentObj.amount.toFixed(prc)) : parseFloat(paymentObj.actualAmount.toFixed(prc)),
            expected: parseFloat(paymentObj.expectedAmount).toFixed(prc),
            diff: parseFloat(paymentObj.difference.toFixed(prc)),
            transactionCount: paymentObj.transactionCount,
            type: "Close",
            description: "",
            cashEventDetails: [],
          },
        ],
      });
    }
  }

  return { tillCash, tillRegistration };
}

// ── Build full request string ──────────────────────────────────────────────────

async function buildSyncRequest(event, tillCash, { tillData, tillSession, closeStoreFlag }) {
  await db.cashInCashOut.clear();

  const prc = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
  const taxInfo = [];
  const ordersDataArr = await db.ordersData.toArray();
  ordersDataArr.forEach((item) => {
    if (item.tillSessionId === event.tillSessionId) {
      (item.salesTax || []).forEach((tax) => {
        taxInfo.push({
          csTaxId: tax.csTaxID,
          amount: parseFloat(tax.taxableAmt.toFixed(prc)),
        });
      });
    }
  });

  let storeOpsTillID;
  const tillEvent = await db.tillEvents.where("tillSessionId").equals(tillSession.tillSessionId).first();
  if (tillEvent) {
    storeOpsTillID = tillEvent.storeDailyOpsTillid;
  }

  const taxInfoData = {
    cwrTillID: tillData.tillAccess.cwrTill.cwrTillID,
    csbunitID: tillData.tillAccess.csBunit.csBunitId,
    csclientID: tillData.tillAccess.csClientId,
    created: event.openingTime,
    createdby: tillData.tillAccess.csUserId,
    updated: event.openingTime,
    updatedby: tillData.tillAccess.csUserId,
    openTill: "N",
    closeStore: closeStoreFlag,
    storeDailyOpsTillid: storeOpsTillID,
    taxInfo,
    tillCash,
  };

  const stringifiedData = JSON.stringify(taxInfoData);
  const newStringifiedFields = stringifiedData.replace(/\\"/g, '\\"');
  return JSON.stringify(newStringifiedFields);
}

// ── Mark event status in DB ────────────────────────────────────────────────────

async function markEventClosed(event, requestString, tillRegistration) {
  await db.ordersData
    .where("tillSessionId")
    .equals(event.tillSessionId)
    .modify((e) => {
      e.tillStatus = "closed";
    });
  await db.paymentsData
    .where("tillSessionId")
    .equals(event.tillSessionId)
    .modify((e) => {
      e.tillStatus = "closed";
    });
  await db.tillEvents
    .where("tillSessionId")
    .equals(event.tillSessionId)
    .modify((e) => {
      e.tillStatus = "closed";
      e.isSynced = 1;
      e.tillRegistration = tillRegistration;
      e.tillInfo = requestString;
      e.user = useAuthStore.getState().userData?.user;
    });
}

async function markEventUnsynced(event) {
  await db.tillEvents
    .where("tillSessionId")
    .equals(event.tillSessionId)
    .modify((e) => {
      e.isSynced = 0;
    });
}

// ── Print receipt ──────────────────────────────────────────────────────────────

async function printCloseReceipt(closingSession, { ObFlag, CWHardwareController, cashValues, paymentsList, cashToKeep, aggregatorSalesData, posConfig }) {
  const keepAmt = cashToKeep && cashToKeep !== "" ? cashToKeep : 0;
  if (ObFlag) {
    await TillCloseHCPrint(closingSession, cashValues, paymentsList, keepAmt);
  } else if (CWHardwareController) {
    await TillCloseCWHCPrint(closingSession, cashValues, paymentsList, keepAmt, aggregatorSalesData, posConfig);
  }
  // else {
  //   tillClosingPrint(closingSession);
  // }
}

// ── Logout ─────────────────────────────────────────────────────────────────────

async function doLogout(domainURL, closeStoreFlag, posConfig, onDayEndOpen) {
  trace("TillClose", "doLogout — clearing cookies + stores");
  // Clear persisted auth data
  useSessionStore.getState().setTillSession(null);
  useSessionStore.getState().setNextBusinessDate(null);
  useAuthStore.getState().logout();

  // CW Browser mode: notify authBus (shell handles redirect)
  if (window.electronAPI?.auth) {
    try {
      await window.electronAPI.auth.logout();
      trace("TillClose", "authBus logout completed");
    } catch (err) {
      trace("TillClose", "authBus logout failed", { error: err?.message });
    }

    // Day-end application — open via callback (iframe modal in CW Browser)
    const dayEndUrl = env.dayEndApplicationUrl;
    if (posConfig?.advanceDayClose === "Y" && closeStoreFlag && dayEndUrl) {
      trace("TillClose", "opening day-end application (CW Browser mode)");
      if (typeof onDayEndOpen === "function") {
        onDayEndOpen(dayEndUrl);
      } else {
        // Fallback: open in new window if no callback provided
        window.open(dayEndUrl, "_blank");
      }
      return;
    }

    // Shell handles sign-in redirect; also navigate client-side as fallback
    // in case the shell redirect does not fire (e.g. previous-day till close).
    window.location.assign("/sign-in");
    return;
  }

  // Web mode (or authBus failed): clear cookies + redirect
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const domainPart = isLocalhost ? "" : `domain=${domainURL};`;
  const securePart = isLocalhost ? "" : "secure=true;";
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const eqPos = cookie.indexOf("=");
    const name = (eqPos > -1 ? cookie.substr(0, eqPos) : cookie).trim();
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;${securePart}SameSite=Strict;${domainPart}`;
  }

  // Day-end application redirect (web mode — full page redirect is fine)
  const dayEndUrl = env.dayEndApplicationUrl;
  if (posConfig?.advanceDayClose === "Y" && closeStoreFlag && dayEndUrl) {
    trace("TillClose", "redirecting to day-end application URL (web mode)");
    if (typeof onDayEndOpen === "function") {
      onDayEndOpen(dayEndUrl);
    } else {
      window.location.href = dayEndUrl;
    }
    return;
  }

  trace("TillClose", "web mode logout — redirecting to sign-in");
  window.location.assign("/sign-in");
}

// ── DI-20: Three-way till session validation ──────────────────────────────────

/**
 * Validates till session data consistency before close.
 * Compares:
 *   1. Sum of individual order totals (orders table) vs ordersData summary grossTotal
 *   2. Sum of payment amounts from allPaymentsData vs ordersData grossTotal
 *   3. Till event opening + transaction amounts balance check
 *
 * Logs discrepancies as warnings but NEVER blocks till close — operators must
 * always be able to close their till even if there are data inconsistencies.
 *
 * @param {string} tillSessionId
 * @returns {{ valid: boolean, violations: Array<Object> }}
 */
export async function validateTillSession(tillSessionId) {
  const violations = [];

  try {
    // 1. Get all orders for this session — sum their individual totals
    const orders = await db.orders.where("tillSessionId").equals(tillSessionId).toArray();
    const orderTotalSum = orders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);

    // 2. Get ordersData summary for this session
    const ordersDataArr = await db.ordersData.toArray();
    const sessionOrdersData = ordersDataArr.filter((d) => d.tillSessionId === tillSessionId);
    const summaryGrossTotal = sessionOrdersData.reduce((sum, d) => sum + (Number(d.grossTotal) || 0), 0);
    const summaryNetTotal = sessionOrdersData.reduce((sum, d) => sum + (Number(d.netTotal) || 0), 0);
    const summaryTotalTax = sessionOrdersData.reduce((sum, d) => sum + (Number(d.totalTax) || 0), 0);

    // Check 1: Individual order totals vs ordersData grossTotal
    if (orders.length > 0 && Math.abs(orderTotalSum - summaryGrossTotal) > 0.01) {
      violations.push({
        check: "orders-vs-ordersData",
        orderTotalSum: Number(orderTotalSum.toFixed(2)),
        summaryGrossTotal: Number(summaryGrossTotal.toFixed(2)),
        diff: Number((orderTotalSum - summaryGrossTotal).toFixed(2)),
        orderCount: orders.length,
      });
    }

    // Check 2: ordersData netTotal + totalTax should equal grossTotal
    const derivedGrossTotal = summaryNetTotal + summaryTotalTax;
    if (sessionOrdersData.length > 0 && Math.abs(derivedGrossTotal - summaryGrossTotal) > 0.01) {
      violations.push({
        check: "ordersData-net-plus-tax-vs-gross",
        netTotal: Number(summaryNetTotal.toFixed(2)),
        totalTax: Number(summaryTotalTax.toFixed(2)),
        derivedGross: Number(derivedGrossTotal.toFixed(2)),
        actualGross: Number(summaryGrossTotal.toFixed(2)),
        diff: Number((derivedGrossTotal - summaryGrossTotal).toFixed(2)),
      });
    }

    // 3. Get till event for this session — check payment amounts vs ordersData
    const tillEvents = await db.tillEvents.where("tillSessionId").equals(tillSessionId).toArray();
    if (tillEvents.length > 0) {
      const tillEvent = tillEvents[0];
      const allPaymentsData = tillEvent.allPaymentsData || [];

      // Sum all expected payment amounts (sales - returns)
      const totalExpectedPayments = allPaymentsData.reduce((sum, p) => sum + (Number(p.expectedAmount) || 0), 0);
      const totalPaymentReturns = allPaymentsData.reduce((sum, p) => sum + (Number(p.paymentReturn) || 0), 0);
      const netPayments = totalExpectedPayments - totalPaymentReturns;

      // Check 3: Payment expected amounts should approximate ordersData grossTotal
      // (there can be legitimate differences due to rounding, cash in/out, etc.)
      if (allPaymentsData.length > 0 && summaryGrossTotal > 0 && Math.abs(netPayments - summaryGrossTotal) > 1.0) {
        violations.push({
          check: "payments-vs-ordersData",
          totalExpectedPayments: Number(totalExpectedPayments.toFixed(2)),
          totalPaymentReturns: Number(totalPaymentReturns.toFixed(2)),
          netPayments: Number(netPayments.toFixed(2)),
          summaryGrossTotal: Number(summaryGrossTotal.toFixed(2)),
          diff: Number((netPayments - summaryGrossTotal).toFixed(2)),
        });
      }
    }

    // Log all violations as warnings — NEVER block till close
    violations.forEach((v) => {
      console.warn("[DI-20] Till close validation discrepancy:", JSON.stringify(v));
    });

    if (violations.length === 0) {
      console.debug("[DI-20] Till session validation passed for:", tillSessionId);
    }
  } catch (err) {
    // Validation errors must NEVER block till close
    console.error("[DI-20] Till session validation failed (non-blocking):", err?.message || err);
  }

  return { valid: violations.length === 0, violations };
}

// ── Main: executeTillSync ──────────────────────────────────────────────────────

/**
 * Full close-till flow:
 *   1. Print receipt IMMEDIATELY (don't block on API)
 *   1b. Validate till session data consistency (warn only, never block)
 *   2. Build payload + call server API for each unsynced event
 *   3. Logout (always, regardless of API result)
 *
 * If API fails, event stays isSynced=0 for retry on next login (via syncOfflineDta).
 */
export async function executeTillSync({
  closingSession,
  tillData,
  tillSession,
  closeStoreFlag,
  noteValue,
  cashToKeep,
  checkedValue,
  cashValues,
  paymentsList,
  shouldUnlink,
  ObFlag,
  CWHardwareController,
  posConfig,
  domainURL,
  unlinkTillFn,
  aggregatorSalesData,
  onDayEndOpen,
  clearCookiesAndLocalStorageFn,
}) {
  // Step 1: Print receipt IMMEDIATELY — don't wait for server
  console.debug("[TillClose] Printing close receipt...");
  try {
    await printCloseReceipt(closingSession, {
      ObFlag,
      CWHardwareController,
      cashValues,
      paymentsList,
      cashToKeep,
      aggregatorSalesData,
      posConfig,
    });
    console.debug("[TillClose] Receipt printed successfully");
  } catch (printErr) {
    console.error("[TillClose] Receipt print failed:", printErr);
  }

  // Step 1b: Validate till session data consistency (warn only, never block)
  try {
    const validation = await validateTillSession(tillSession.tillSessionId);
    if (!validation.valid) {
      console.warn("[TillClose] Session has", validation.violations.length, "data discrepancies — proceeding with close anyway");
    }
  } catch (valErr) {
    console.error("[TillClose] Validation error (non-blocking):", valErr?.message || valErr);
  }

  // Step 2: Get unsynced events and sync to server
  const unsyncedEvents = await getUnsyncedEvents();
  console.debug("[TillClose] Found", unsyncedEvents.length, "unsynced events to process");

  let didLogout = false;

  if (unsyncedEvents.length > 0) {
    for (const event of unsyncedEvents) {
      try {
        console.debug("[TillClose] Processing session:", event.tillSessionId);

        const { tillCash, tillRegistration } = await buildTillCashForEvent(event, {
          tillData,
          noteValue,
          cashToKeep,
          checkedValue,
          posConfig,
        });

        const requestString = await buildSyncRequest(event, tillCash, {
          tillData,
          tillSession,
          closeStoreFlag,
        });

        // Mark closed + store tillInfo in DB
        await markEventClosed(event, requestString, tillRegistration);

        // Call server API
        console.debug("[TillClose] Calling upsertTill API for session:", event.tillSessionId);
        const tillRes = await upsertTill(requestString);
        const result = tillRes?.data?.data?.upsertTill;
        const status = result?.status;
        if (status === "200") {
          console.debug("[TillClose] API success for session:", event.tillSessionId);
          // Sync POS activity (non-blocking)
          try {
            const posActivityRes = await upsertPOSActivity(tillRegistration);
            if (posActivityRes.data.data.upsertPOSActivity.status === 200 || posActivityRes.data.data.upsertPOSActivity.status === "200") {
              // Logout must run unconditionally. posConfig.closingRegister never actually
              // gated this in the original (pre-refactor) flow — it sat beside an unconditional
              // duplicate that always ran the same logout, so closingRegister=Y just double-fired
              // harmlessly. Gating on it here (instead of removing the dead duplicate) meant any
              // tenant with closingRegister !== "Y" (the documented default) never logs out at
              // all, leaving the close-till screen stuck on "Closing...".
              try {
                const shouldUnlinkTill = useSessionStore.getState().unlink;
                didLogout = true;
                if (shouldUnlinkTill) {
                  unlinkTillFn(closeStoreFlag);
                } else {
                  clearCookiesAndLocalStorageFn(closeStoreFlag);
                }
              } catch (error) {
                didLogout = true;
                window.location.assign("/sign-in");
              }
            }
          } catch (actErr) {
            console.debug("[TillClose] POS Activity sync failed (non-blocking):", actErr);
          }
        } else {
          console.error("[TillClose] API returned non-200:", status, result?.message);
          await markEventUnsynced(event);
        }
      } catch (error) {
        console.error("[TillClose] Sync failed for session:", event.tillSessionId, error.message || error);
        try {
          await markEventUnsynced(event);
        } catch (markErr) {
          console.error("[TillClose] Failed to mark event unsynced:", markErr);
        }
      }
    }
  }

  // Step 3: Fallback logout — fires only when the loop never triggered logout.
  // Covers (a) 0 unsynced events and (b) all events failed the API call.
  if (!didLogout) {
    if (shouldUnlink) {
      unlinkTillFn(closeStoreFlag);
    } else {
      doLogout(domainURL, closeStoreFlag, posConfig, onDayEndOpen);
    }
  }
}
